import * as csc from 'cornerstone-core';
import { getToolState, addToolState, getToolForElement, EVENTS } from 'cornerstone-tools';
import * as _ from 'lodash';
import { getImageFitScale } from '../utils/cornerstone/imageUtils';
import { cropCursor } from './Cursors';
import { forceImageUpdate } from './forceImageUpdate';
import injectSwitchableTool from './injectSwitchableTool';
import { projectAlongAxis } from './pixelToCanvasUtils';
import { removeToolState, typedCornerstoneToolsImport } from 'app/CornerstoneTools';
import {
  CropAnnotation,
  Point,
  RenderEventWithCornerstoneDetail,
} from 'app/CornerstoneTools/types';
import { Rect } from 'app/interfaces/Image';

const CROP_EVENTS = {
  CROP_ACTIVATED: 'cornerstone_crop_activated',
  CROP_CONFIRMED: 'cornerstone_crop_confirmed',
  CROP_CANCELED: 'cornerstone_crop_canceled',
};

const BaseAnnotationTool = typedCornerstoneToolsImport('base/BaseAnnotationTool');
const getNewContext = typedCornerstoneToolsImport('drawing/getNewContext');
const draw = typedCornerstoneToolsImport('drawing/draw');
const drawLine = typedCornerstoneToolsImport('drawing/drawLine');
const drawLines = typedCornerstoneToolsImport('drawing/drawLines');
const isPointInPolygon = typedCornerstoneToolsImport('util/isPointInPolygon');
const triggerEvent = typedCornerstoneToolsImport('util/triggerEvent');

const POINTS_LINKS: Record<string, { x: string[]; y: string[] }> = {
  topLeft: { x: ['bottomLeft', 'left'], y: ['topRight', 'top'] },
  topRight: { x: ['end', 'right'], y: ['topLeft', 'top'] },
  bottomLeft: { x: ['topLeft', 'left'], y: ['end', 'bottom'] },
  end: { x: ['topRight', 'right'], y: ['bottomLeft', 'bottom'] },
  top: { x: [], y: ['topLeft', 'topRight'] },
  left: { x: ['bottomLeft', 'topLeft'], y: [] },
  right: { x: ['topRight', 'end'], y: [] },
  bottom: { x: [], y: ['end', 'bottomLeft'] },
};

const MIDDLE_POINTS_LINKS = { top: 'y', left: 'x', right: 'x', bottom: 'y' } as Record<
  string,
  'x' | 'y' | undefined
>;

const ZERO_INIT_POINT = {
  x: 0,
  y: 0,
  highlight: true,
  active: false,
  allowedOutsideImage: true,
};

const isCropToolActive = (element: HTMLElement, toolName: string) => {
  const tool = getToolForElement(element, toolName);
  return tool.mode === 'active';
};

const getCropRect = (element: HTMLElement, toolName: string) => {
  const toolData = getToolState(element, toolName);
  if (!toolData || toolData.data.length === 0) {
    return null;
  }
  const { topLeft, end } = toolData.data[0].handles;
  return {
    x: Math.min(topLeft.x, end.x),
    y: Math.min(topLeft.y, end.y),
    width: Math.abs(topLeft.x - end.x),
    height: Math.abs(topLeft.y - end.y),
  };
};

const getCropMinScale = (element: HTMLElement, toolName: string) => {
  if (isCropToolActive(element, toolName)) return null;

  const { canvas, viewport } = csc.getEnabledElement(element);

  const cropRect = getCropRect(element, toolName);
  if (cropRect) {
    return getImageFitScale(canvas, cropRect, viewport.rotation).scaleFactor;
  }
  return null;
};

function injectBoundingBoxToHandles(element: HTMLElement, handles: CropAnnotation['handles']) {
  const handleKeys = Object.keys(handles);

  handleKeys.forEach((key) => {
    const handle = handles[key];
    if (MIDDLE_POINTS_LINKS[key]) {
      return;
    }

    const canvasPosition = csc.pixelToCanvas(element, handle as csc.PixelCoordinate);

    const boundingBoxSize = 20;
    const halfBoundingBoxSize = boundingBoxSize / 2;

    handle.hasBoundingBox = true;
    handle.boundingBox = {
      left: canvasPosition.x - halfBoundingBoxSize,
      top: canvasPosition.y - halfBoundingBoxSize,
      width: boundingBoxSize,
      height: boundingBoxSize,
    };
  });
}
function removeBoundingBoxFromHandles(element: HTMLElement, handles: CropAnnotation['handles']) {
  const handleKeys = Object.keys(handles);

  handleKeys.forEach((key) => {
    const handle = handles[key];

    delete handle.boundingBox;
    delete handle.hasBoundingBox;
  });
}

export const computeCropFocusViewport = (
  handles: CropAnnotation['handles'],
  { image, viewport, canvas }: Pick<csc.EnabledElement, 'image' | 'viewport' | 'canvas'> = {}
) => {
  if (!handles) return viewport;

  const { topLeft, end } = handles;

  // A bit strange here, but since changing scaling will zoom the image onto the
  // center of the image we have to compute our offset from the center of the
  // image instead of from an edge.
  const cropCenterX = Math.min(end.x, topLeft.x) + Math.abs(end.x - topLeft.x) / 2;
  const cropCenterY = Math.min(end.y, topLeft.y) + Math.abs(end.y - topLeft.y) / 2;

  const imageCenterX = image.width / 2;
  const imageCenterY = image.height / 2;

  let translation = {
    x: (imageCenterX - cropCenterX) * (viewport.hflip ? -1 : 1),
    y: (imageCenterY - cropCenterY) * (viewport.vflip ? -1 : 1),
  };

  translation = {
    x:
      Math.cos(viewport.rotation * (Math.PI / 180)) * translation.x -
      Math.sin(viewport.rotation * (Math.PI / 180)) * translation.y,
    y:
      Math.sin(viewport.rotation * (Math.PI / 180)) * translation.x +
      Math.cos(viewport.rotation * (Math.PI / 180)) * translation.y,
  };

  const windowSize = {
    width: Math.abs(end.x - topLeft.x),
    height: Math.abs(end.y - topLeft.y),
  };
  const newImageRegion = _.merge({}, image, windowSize);
  const { scaleFactor } = getImageFitScale(canvas, newImageRegion, viewport.rotation);

  const updatedViewport = _.merge({}, viewport, {
    scale: scaleFactor,
    translation,
  });

  return updatedViewport;
};

const focusHandles = (element: HTMLElement, handles: CropAnnotation['handles']) => {
  const { canvas, image, viewport } = csc.getEnabledElement(element);

  const updatedViewport = computeCropFocusViewport(handles, { canvas, image, viewport });

  if (_.isEqual(updatedViewport, viewport)) return;

  csc.setViewport(element, updatedViewport);
  return updatedViewport;
};

const roundCoords = (coords: Point) => ({ x: Math.round(coords.x), y: Math.round(coords.y) });

const isCropInProgress = (element: HTMLElement, toolName = 'Crop') => {
  const cropToolData = getToolState(element, toolName);
  return cropToolData?.data?.[0]?.previousHandles !== undefined;
};

const syncHandles = (draftHandles: CropAnnotation['handles']) => {
  _.forEach(draftHandles, ({ x, y, active }, key) => {
    if (!active) return;
    POINTS_LINKS[key].x.forEach((handleKey) => {
      draftHandles[handleKey].x = x;
    });
    POINTS_LINKS[key].y.forEach((handleKey) => {
      draftHandles[handleKey].y = y;
    });
  });

  _.forEach(draftHandles, (draftHandle, key) => {
    if (MIDDLE_POINTS_LINKS[key] === undefined) return;
    const edgesHandle = POINTS_LINKS[key][MIDDLE_POINTS_LINKS[key]];
    draftHandle.x = (draftHandles[edgesHandle[0]].x + draftHandles[edgesHandle[1]].x) / 2;
    draftHandle.y = (draftHandles[edgesHandle[0]].y + draftHandles[edgesHandle[1]].y) / 2;
  });
  return draftHandles;
};

function computeBoundingBox(points: Point[], shouldRound = true): Rect {
  const xs = points.map((p) => (shouldRound ? Math.round(p.x) : p.x));
  const ys = points.map((p) => (shouldRound ? Math.round(p.y) : p.y));
  const minX = Math.min(...xs);
  const maxX = Math.max(...xs);
  const minY = Math.min(...ys);
  const maxY = Math.max(...ys);

  return {
    x: minX,
    y: minY,
    width: maxX - minX,
    height: maxY - minY,
  };
}

function associateBoundingBoxesToHandles(
  boundingBox: Rect,
  cropPoints: [Point, Point, Point, Point]
) {
  const maxX = boundingBox.x + boundingBox.width;
  const maxY = boundingBox.y + boundingBox.height;
  // We use a minBy, maxBy to get the closest point to corner
  const upperLeft = _.minBy(
    _.filter(cropPoints, (point) => point.y === boundingBox.y),
    'x'
  );
  const upperRight = _.minBy(
    _.filter(cropPoints, (point) => point.x === maxX),
    'y'
  );
  const lowerLeft = _.maxBy(
    _.filter(cropPoints, (point) => point.x === boundingBox.x),
    'y'
  );
  const lowerRight = _.maxBy(
    _.filter(cropPoints, (point) => point.y === maxY),
    'x'
  );

  return {
    upperLeft,
    upperRight,
    lowerLeft,
    lowerRight,
  };
}

export const convertRectToCropHandles = ([x, y, width, height]: [number, number, number, number]): {
  handles: CropAnnotation['handles'];
} => ({
  handles: syncHandles({
    topLeft: {
      x,
      y,
      highlight: true,
      active: true,
      allowedOutsideImage: true,
    },
    top: _.clone(ZERO_INIT_POINT),
    left: _.clone(ZERO_INIT_POINT),
    right: _.clone(ZERO_INIT_POINT),
    bottom: _.clone(ZERO_INIT_POINT),
    topRight: _.clone(ZERO_INIT_POINT),
    bottomLeft: _.clone(ZERO_INIT_POINT),
    end: {
      x: x + width,
      y: y + height,
      highlight: true,
      active: true,
      allowedOutsideImage: true,
    },
  }),
});

class CropTool extends BaseAnnotationTool {
  constructor(props = {}) {
    const defaultProps = {
      name: 'Crop',
      supportedInteractionTypes: ['Mouse', 'Touch'],
      configuration: {
        drawHandles: true,
        drawHandlesOnHover: false,
        hideHandlesIfMoving: false,
        renderDashed: false,
        clickProximity: 15,
        touchProximity: 20,
      },
      svgCursor: cropCursor,
    };

    super(props, defaultProps);

    this.preventNewMeasurement = false;

    this.zeroInitPoint = {
      ...ZERO_INIT_POINT,
    };
  }

  // eslint-disable-next-line class-methods-use-this
  createNewMeasurement = () => {};

  cropOnRect = (element: HTMLElement, [x, y, width, height]: [number, number, number, number]) => {
    const measurementData = convertRectToCropHandles([x, y, width, height]);

    this.correctHandles(element, measurementData.handles);
    measurementData.handles.topLeft.active = false;
    measurementData.handles.end.active = false;

    const toolData = getToolState(element, this.name);

    if (toolData && toolData.data.length > 0) {
      _.merge(toolData.data[0], measurementData);
    } else {
      addToolState(element, this.name, measurementData);
    }

    this.validateCrop(element);
    forceImageUpdate(element);
  };

  activeCallback = (element: HTMLElement) => {
    const enabledElement = csc.getEnabledElement(element);
    const toolData = getToolState(element, this.name);

    if (toolData && toolData.data.length > 0) {
      toolData.data[0].previousHandles = _.cloneDeep(toolData.data[0].handles ?? null);

      triggerEvent(element, CROP_EVENTS.CROP_ACTIVATED, {
        toolName: this.name,
        toolType: this.name,
        element,
        measurementData: toolData.data[0],
      });
      csc.resize(element, true);
      forceImageUpdate(element);
      return;
    }
    const { image } = enabledElement;

    const measurementData: CropAnnotation = {
      visible: true,
      active: true,
      color: undefined,
      invalidated: true,
      previousHandles: null,
      handles: {
        topLeft: {
          x: 0,
          y: 0,
          highlight: true,
          active: true,
          allowedOutsideImage: true,
        },
        top: _.clone(this.zeroInitPoint),
        left: _.clone(this.zeroInitPoint),
        right: _.clone(this.zeroInitPoint),
        bottom: _.clone(this.zeroInitPoint),
        topRight: _.clone(this.zeroInitPoint),
        bottomLeft: _.clone(this.zeroInitPoint),
        end: {
          x: image.width,
          y: image.height,
          highlight: true,
          active: true,
          allowedOutsideImage: true,
        },
      },
    };
    this.correctHandles(element, measurementData.handles);
    measurementData.handles.topLeft.active = false;
    measurementData.handles.end.active = false;
    addToolState(element, this.name, measurementData);
    triggerEvent(element, CROP_EVENTS.CROP_ACTIVATED, {
      toolName: this.name,
      toolType: this.name,
      element,
      measurementData,
    });
    csc.resize(element, true);
    forceImageUpdate(element);
  };

  passiveCallback = (element: HTMLElement) => {
    this.validateCrop(element);
  };

  validateCrop = (element: HTMLElement) => {
    const toolData = getToolState(element, this.name);

    if (!toolData || toolData.data.length === 0) return;
    const alreadyValidated = toolData.data[0].previousHandles === undefined;
    if (alreadyValidated) return;
    delete toolData.data[0].previousHandles;
    focusHandles(element, toolData.data[0].handles);
    this.fireCompleted(element, toolData.data[0]);
  };

  cancelCrop = (element: HTMLElement) => {
    const toolData = getToolState(element, this.name);

    if (!toolData || toolData.data.length === 0) return;
    if (toolData.data[0].previousHandles) {
      toolData.data[0].handles = toolData.data[0].previousHandles;
      delete toolData.data[0].previousHandles;
      focusHandles(element, toolData.data[0].handles);
      this.fireCompleted(element, toolData.data[0]);
    } else {
      removeToolState(element, this.name, toolData.data[0]);
    }
  };

  fireCompleted = (element: HTMLElement, measurementData: CropAnnotation) => {
    triggerEvent(element, EVENTS.MEASUREMENT_COMPLETED, {
      toolName: this.name,
      toolType: this.name,
      element,
      measurementData,
    });
  };

  // eslint-disable-next-line class-methods-use-this
  updateCachedStats(image: csc.Image, element: HTMLElement, annotation: CropAnnotation) {
    // THis is called every time the handle is updated, we can use this to ensure the handles are always correct and in sync with the bounding box.
    // This avoid malformed crop creation when moving the mouse too fast last active state for moved handle being missed.
    this.correctHandles(element, annotation.handles);
  }

  // eslint-disable-next-line class-methods-use-this
  pointNearTool = (element: HTMLElement, data: CropAnnotation, coords: Point) => {
    const tool = getToolForElement(element, this.name);
    const isActive = tool.mode === 'active';

    if (!isActive) return false;

    const { topLeft, topRight, bottomLeft, end } = data.handles;
    const rect = [
      [topLeft.x, topLeft.y],
      [topRight.x, topRight.y],
      [end.x, end.y],
      [bottomLeft.x, bottomLeft.y],
    ];
    const pixelCoords = csc.canvasToPixel(element, coords as csc.CanvasCoordinate);

    return isPointInPolygon([pixelCoords.x, pixelCoords.y], rect);
  };

  /* eslint-disable no-param-reassign */
  correctHandles = (element: HTMLElement, handles: CropAnnotation['handles']) => {
    const { image } = csc.getEnabledElement(element);

    _.forEach(handles, (handle) => {
      handle.x = Math.min(Math.max(handle.x, 0), image.width);
      handle.y = Math.min(Math.max(handle.y, 0), image.height);
    });
    syncHandles(handles);
  };
  /* eslint-enable no-param-reassign */

  renderMaskingArea = (
    ctx: CanvasRenderingContext2D,
    element: HTMLElement,
    data: CropAnnotation,
    fillStyle: string
  ) => {
    const { canvas } = ctx;
    const { topLeft, end, topRight, bottomLeft } = data.handles;

    const { x, y, width, height } = computeBoundingBox([topLeft, topRight, bottomLeft, end]);

    const upperLeft = roundCoords(csc.pixelToCanvas(element, { x, y } as csc.PixelCoordinate));
    const upperRight = roundCoords(
      csc.pixelToCanvas(element, { x: x + width, y } as csc.PixelCoordinate)
    );
    const lowerRight = roundCoords(
      csc.pixelToCanvas(element, { x: x + width, y: y + height } as csc.PixelCoordinate)
    );
    const lowerLeft = roundCoords(
      csc.pixelToCanvas(element, { x, y: y + height } as csc.PixelCoordinate)
    );

    const canvasUpperLeft = { x: 0, y: 0 };
    const canvasUpperRight = { x: canvas.width, y: 0 };
    const canvasLowerRight = { x: canvas.width, y: canvas.height };
    const canvasLowerLeft = { x: 0, y: canvas.height };

    ctx.fillStyle = fillStyle;

    let cropHole = new Path2D();
    // Outer rectangle (canvas bounds) - clockwise
    cropHole.moveTo(canvasUpperLeft.x, canvasUpperLeft.y);
    cropHole.lineTo(canvasUpperRight.x, canvasUpperRight.y);
    cropHole.lineTo(canvasLowerRight.x, canvasLowerRight.y);
    cropHole.lineTo(canvasLowerLeft.x, canvasLowerLeft.y);
    cropHole.closePath();

    const { hflip, vflip } = csc.getEnabledElement(element)?.viewport ?? {};

    const isRotationInverted = hflip !== vflip;
    // Inner rectangle (crop area) - counter-clockwise to create a hole
    cropHole.moveTo(upperLeft.x, upperLeft.y);
    if (isRotationInverted) {
      cropHole.lineTo(upperRight.x, upperRight.y);
      cropHole.lineTo(lowerRight.x, lowerRight.y);
      cropHole.lineTo(lowerLeft.x, lowerLeft.y);
    } else {
      cropHole.lineTo(lowerLeft.x, lowerLeft.y);
      cropHole.lineTo(lowerRight.x, lowerRight.y);
      cropHole.lineTo(upperRight.x, upperRight.y);
    }
    cropHole.closePath();
    ctx.fill(cropHole, 'evenodd');
  };

  drawCropBorder = (ctx: CanvasRenderingContext2D, element: HTMLElement, data: CropAnnotation) => {
    const { topLeft, topRight, bottomLeft, end, top, left, right, bottom } = data.handles;
    const lineOptions = {
      color: 'rgba(0, 0, 0, 0.6)',
      lineWidth: 5,
    };
    ctx.save();

    drawLine(ctx, element, topLeft, topRight, lineOptions);
    drawLine(ctx, element, topLeft, bottomLeft, lineOptions);
    drawLine(ctx, element, end, bottomLeft, lineOptions);
    drawLine(ctx, element, end, topRight, lineOptions);

    this.drawBorderHandles(ctx, element, topLeft, top, topRight, 10);
    this.drawBorderHandles(ctx, element, topLeft, left, bottomLeft, 10);
    this.drawBorderHandles(ctx, element, topRight, right, end, 10);
    this.drawBorderHandles(ctx, element, bottomLeft, bottom, end, 10);
    this.drawBorderHandles(ctx, element, bottomLeft, topLeft, topRight, 10);
    this.drawBorderHandles(ctx, element, end, bottomLeft, topLeft, 10);
    this.drawBorderHandles(ctx, element, topRight, end, bottomLeft, 10);
    this.drawBorderHandles(ctx, element, topLeft, topRight, end, 10);

    ctx.restore();
  };

  drawBorderHandles = (
    ctx: CanvasRenderingContext2D,
    element: HTMLElement,
    start: Point,
    middle: Point,
    end: Point,
    lineLength: number
  ) => {
    const middlePoint = csc.pixelToCanvas(element, roundCoords(middle) as csc.PixelCoordinate);

    const startPoint = csc.pixelToCanvas(
      element,
      projectAlongAxis(
        element,
        roundCoords(middle),
        roundCoords(middle),
        roundCoords(start),
        lineLength
      ) as csc.PixelCoordinate
    );
    const endPoint = csc.pixelToCanvas(
      element,
      projectAlongAxis(
        element,
        roundCoords(middle),
        roundCoords(middle),
        roundCoords(end),
        lineLength
      ) as csc.PixelCoordinate
    );

    const drawBorder = () => {
      ctx.beginPath();
      ctx.moveTo(Math.round(startPoint.x), Math.round(startPoint.y));
      ctx.lineTo(Math.round(middlePoint.x), Math.round(middlePoint.y));
      ctx.lineTo(Math.round(endPoint.x), Math.round(endPoint.y));
      ctx.stroke();
    };

    ctx.strokeStyle = 'white';
    ctx.lineCap = 'square';
    ctx.lineWidth = 7;
    drawBorder();

    ctx.strokeStyle = 'black';
    ctx.lineWidth = 5;
    drawBorder();
  };

  renderToolData = (evt: RenderEventWithCornerstoneDetail) => {
    const eventData = evt.detail;
    const { element } = eventData;

    // If we have no toolData for this element, return immediately as there is nothing to do
    const toolData = getToolState(evt.currentTarget, this.name);

    if (!toolData) {
      return;
    }

    const tool = getToolForElement(element, this.name);
    const isActive = tool.mode === 'active';

    // We have tool data for this element - iterate over each one and draw it
    const context = getNewContext(eventData.canvasContext.canvas);
    draw(context, (ctx: CanvasRenderingContext2D) => {
      for (let i = 0; i < toolData.data.length; i += 1) {
        const data = toolData.data[i];

        const maskColor = isActive ? 'rgba(255,255,255,0.4)' : 'black';
        this.renderMaskingArea(ctx, element, data, maskColor);
        // Draw the corners
        if (this.configuration.drawHandles && isActive) {
          this.drawCropBorder(ctx, element, data);
        }
      }
    });
  };
}

export default injectSwitchableTool(CropTool);

export {
  isCropToolActive,
  getCropRect,
  getCropMinScale,
  focusHandles,
  CROP_EVENTS,
  isCropInProgress,
};
